import {
  X,
  User,
  Mail,
  Phone,
  Calendar,
  FileText,
  ClipboardList,
  Download,
  MessageSquare,
  Hash,
  Clock,
  ShieldCheck,
  CheckCircle2,
  Activity,
  CreditCard,
  History,
} from 'lucide-react'
import { Badge, Button } from '@/components/ui'
import type { AdminUser, ApprovalRequest } from '../../types/admin.types'
import { useAdminUserActivity } from '../../hooks/use-admin'

export interface UserDetailDrawerProps {
  isOpen: boolean
  onClose: () => void
  /** For backward-compat: can still be driven by just a user */
  user?: AdminUser | null
  /** Full approval request — shows applicationData, uploadedDocuments etc. */
  request?: ApprovalRequest | null
}

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType
  label: string
  value: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-slate-200 dark:border-border/40 p-3 bg-slate-50 dark:bg-muted/20">
      <Icon className="size-4 text-primary shrink-0 mt-0.5" />
      <div className="min-w-0">
        <p className="text-xs text-slate-500 dark:text-muted-foreground">{label}</p>
        <p className="font-semibold text-slate-900 dark:text-foreground text-sm break-words">{value}</p>
      </div>
    </div>
  )
}

function humanize(key: string): string {
  return key
    .replace(/^doc_/, '')
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()
}

export function UserDetailDrawer({ isOpen, onClose, user: propUser, request }: UserDetailDrawerProps) {
  if (!isOpen) return null

  // Derive initial user
  const initialUser = propUser ?? request?.user ?? null
  const userId = initialUser?.id

  // Fetch full live user activity from API
  const { data: activityData, isLoading: isLoadingActivity } = useAdminUserActivity(userId)

  const user = activityData?.user ?? initialUser
  if (!user) return null

  // Collect approval requests (either from full activity query or single request prop)
  const approvalRequests: ApprovalRequest[] = activityData?.approvalRequests ?? (request ? [request] : [])
  const auditLogs: any[] = activityData?.auditLogs ?? []
  const lastLoginAt = activityData?.lastLoginAt ?? (user as any).lastLoginAt
  const isEmailVerified = activityData?.isEmailVerified ?? user.isEmailVerified

  // Main application form data from active or latest request
  const activeRequest = approvalRequests[0] ?? request ?? null
  const appData: Record<string, any> = activeRequest?.applicationData ?? {}
  const uploadedDocs = activeRequest?.uploadedDocuments ?? []
  const remarks = activeRequest?.remarks ?? null
  const reviewNotes = activeRequest?.reviewNotes ?? null

  // Build entries from applicationData for display
  const appDataEntries = Object.entries(appData).filter(
    ([, v]) => v !== null && v !== undefined && v !== '' && typeof v !== 'object'
  )

  const snapshotFields: { name: string; label: string }[] =
    activeRequest?.formConfigSnapshot?.fields ?? []
  const getLabel = (key: string) =>
    snapshotFields.find((f) => f.name === key)?.label ?? humanize(key)

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg h-full bg-white dark:bg-card border-l border-slate-200 dark:border-border/80 flex flex-col shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300">
        
        {/* ── Header ── */}
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-border/60 p-5 sticky top-0 bg-white dark:bg-card z-10">
          <div className="flex items-center gap-3">
            <div className="flex size-12 items-center justify-center rounded-xl bg-primary/10 text-primary font-display text-lg font-bold shrink-0">
              {user.firstName?.[0]}
              {user.lastName?.[0]}
            </div>
            <div>
              <h3 className="font-display text-base font-bold text-slate-900 dark:text-foreground">
                {user.firstName} {user.lastName}
              </h3>
              <p className="text-xs text-slate-500 dark:text-muted-foreground">{user.email}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="size-5" />
          </Button>
        </div>

        <div className="flex-1 p-5 space-y-6">

          {/* ── Status Badges ── */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-200 dark:border-border/60 bg-slate-50 dark:bg-background/50 p-3 space-y-1">
              <span className="text-[10px] uppercase font-bold text-slate-500 dark:text-muted-foreground">Role</span>
              <div>
                <Badge variant="default">{user.role}</Badge>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 dark:border-border/60 bg-slate-50 dark:bg-background/50 p-3 space-y-1">
              <span className="text-[10px] uppercase font-bold text-slate-500 dark:text-muted-foreground">Account Status</span>
              <div>
                <Badge
                  variant={
                    user.approvalStatus === 'ACTIVE' || user.approvalStatus === 'APPROVED'
                      ? 'success'
                      : user.approvalStatus === 'PENDING'
                      ? 'warning'
                      : 'destructive'
                  }
                >
                  {user.approvalStatus}
                </Badge>
              </div>
            </div>
          </div>

          {/* ── Action & Security Timeline Summary ── */}
          <div className="space-y-3">
            <h4 className="font-display text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-muted-foreground flex items-center gap-1.5">
              <Clock className="size-3.5" /> User Activity & Milestones
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              {/* Registration Date */}
              <div className="p-3 rounded-xl border border-slate-200 dark:border-border/50 bg-slate-50 dark:bg-background/50 space-y-1">
                <span className="text-[10px] text-slate-500 dark:text-muted-foreground font-semibold flex items-center gap-1">
                  <Calendar className="size-3 text-primary" /> Registered On
                </span>
                <p className="font-bold text-slate-900 dark:text-foreground">
                  {new Date(user.createdAt).toLocaleDateString(undefined, {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </p>
              </div>

              {/* Email Verification Status */}
              <div className="p-3 rounded-xl border border-slate-200 dark:border-border/50 bg-slate-50 dark:bg-background/50 space-y-1">
                <span className="text-[10px] text-slate-500 dark:text-muted-foreground font-semibold flex items-center gap-1">
                  <ShieldCheck className="size-3 text-emerald-500" /> Email Verification
                </span>
                <p className="font-bold text-slate-900 dark:text-foreground flex items-center gap-1">
                  {isEmailVerified ? (
                    <span className="text-emerald-600 dark:text-emerald-500 flex items-center gap-1">
                      <CheckCircle2 className="size-3.5" /> Verified
                    </span>
                  ) : (
                    <span className="text-amber-600 dark:text-amber-500">Unverified</span>
                  )}
                </p>
              </div>

              {/* Last Login Timestamp */}
              <div className="p-3 rounded-xl border border-slate-200 dark:border-border/50 bg-slate-50 dark:bg-background/50 space-y-1">
                <span className="text-[10px] text-slate-500 dark:text-muted-foreground font-semibold flex items-center gap-1">
                  <Activity className="size-3 text-blue-500" /> Last Login
                </span>
                <p className="font-bold text-slate-900 dark:text-foreground">
                  {lastLoginAt ? new Date(lastLoginAt).toLocaleString() : 'Never / Active Session'}
                </p>
              </div>

              {/* Application Form Status */}
              <div className="p-3 rounded-xl border border-slate-200 dark:border-border/50 bg-slate-50 dark:bg-background/50 space-y-1">
                <span className="text-[10px] text-slate-500 dark:text-muted-foreground font-semibold flex items-center gap-1">
                  <ClipboardList className="size-3 text-purple-500" /> Submitted Application
                </span>
                <p className="font-bold text-slate-900 dark:text-foreground">
                  {activeRequest ? (
                    <span>
                      {activeRequest.requestType} ({activeRequest.status})
                    </span>
                  ) : (
                    <span className="text-slate-400 font-normal">None Submitted</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* ── Account Information ── */}
          <div className="space-y-3">
            <h4 className="font-display text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-muted-foreground flex items-center gap-1.5">
              <User className="size-3.5" /> Contact Details
            </h4>
            <InfoRow icon={User} label="Full Name" value={`${user.firstName} ${user.lastName}`} />
            <InfoRow icon={Mail} label="Email Address" value={user.email} />
            <InfoRow icon={Phone} label="Phone Number" value={user.phone ?? 'Not provided'} />
          </div>

          {/* ── Submitted Form Data & Answers ── */}
          {appDataEntries.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-display text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-muted-foreground flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <ClipboardList className="size-3.5 text-primary" /> Submitted Form Answers
                </span>
                {activeRequest?.formVersion && (
                  <Badge variant="outline" className="text-[10px]">
                    v{activeRequest.formVersion}
                  </Badge>
                )}
              </h4>

              <div className="space-y-2">
                {appDataEntries.map(([key, val]) => (
                  <div
                    key={key}
                    className="flex items-start gap-3 rounded-lg border border-slate-200 dark:border-border/40 p-3 bg-slate-50 dark:bg-muted/20"
                  >
                    <Hash className="size-3.5 text-primary shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-slate-500 dark:text-muted-foreground font-medium">
                        {getLabel(key)}
                      </p>
                      <p className="font-semibold text-slate-900 dark:text-foreground text-sm break-words mt-0.5">
                        {typeof val === 'boolean' ? (val ? 'Yes' : 'No') : String(val)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Uploaded Application Documents ── */}
          {uploadedDocs.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-display text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-muted-foreground flex items-center gap-1.5">
                <FileText className="size-3.5 text-rose-500" /> Uploaded Verification Documents
              </h4>
              <div className="space-y-2">
                {uploadedDocs.map((doc: any, idx: number) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between rounded-lg border border-dashed border-rose-300 dark:border-rose-500/40 bg-rose-50 dark:bg-rose-500/5 p-3 gap-2"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="size-4 text-rose-500 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-slate-800 dark:text-foreground truncate">
                          {doc.fileName || doc.field}
                        </p>
                        <p className="text-[10px] text-slate-500 dark:text-muted-foreground">
                          {doc.field && <span className="capitalize">{humanize(doc.field)} · </span>}
                          {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : 'Uploaded'}
                          {doc.version > 1 && <span className="ml-1">v{doc.version}</span>}
                        </p>
                      </div>
                    </div>
                    {doc.url && (
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noreferrer"
                        className="shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white dark:bg-background border border-slate-200 dark:border-border/60 text-[11px] font-bold text-primary hover:bg-primary/5 transition-colors"
                      >
                        <Download className="size-3" /> View
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Applicant Remarks ── */}
          {remarks && (
            <div className="space-y-2">
              <h4 className="font-display text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-muted-foreground flex items-center gap-1.5">
                <MessageSquare className="size-3.5" /> Applicant Remarks
              </h4>
              <div className="rounded-xl border border-slate-200 dark:border-border/60 bg-slate-50 dark:bg-background/50 p-3 text-sm text-slate-700 dark:text-foreground/90 italic leading-relaxed">
                "{remarks}"
              </div>
            </div>
          )}

          {/* ── Admin Review Notes (if reviewed) ── */}
          {reviewNotes && (
            <div className="rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-4 space-y-1">
              <p className="text-xs font-bold text-amber-700 dark:text-amber-500 uppercase tracking-wide">
                Admin Review Notes
              </p>
              <p className="text-sm text-slate-700 dark:text-foreground/90 leading-relaxed">{reviewNotes}</p>
            </div>
          )}

          {/* ── Detailed Audit Trail Logs ── */}
          {auditLogs.length > 0 && (
            <div className="space-y-3 pt-2">
              <h4 className="font-display text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-muted-foreground flex items-center gap-1.5">
                <History className="size-3.5 text-primary" /> User Audit & Action Trail
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {auditLogs.map((log: any, lIdx: number) => (
                  <div
                    key={log.id || lIdx}
                    className="p-2.5 rounded-lg border border-slate-200 dark:border-border/40 bg-slate-50/70 dark:bg-background/50 text-xs space-y-1"
                  >
                    <div className="flex items-center justify-between font-bold text-slate-800 dark:text-foreground">
                      <span className="text-primary uppercase text-[10px] tracking-wide font-mono">
                        {log.action}
                      </span>
                      <span className="text-[10px] text-slate-400 font-normal">
                        {new Date(log.createdAt).toLocaleString()}
                      </span>
                    </div>
                    {log.details && (
                      <p className="text-[11px] text-slate-500 dark:text-muted-foreground font-mono truncate">
                        {JSON.stringify(log.details)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* ── Footer ── */}
        <div className="p-5 border-t border-slate-200 dark:border-border/60">
          <Button variant="outline" onClick={onClose} className="w-full font-semibold">
            Close Overview
          </Button>
        </div>
      </div>
    </div>
  )
}
